import { z } from 'zod';

import {
  VoucherStatusSchema,
  FlightSegmentSchema,
  HotelDetailsSchema,
  BusSegmentSchema,
  RentalDetailsSchema,
  MobilityDetailsSchema,
} from '@voll-intelligence/domain';

/**
 * O voucher como a conversa o mostra — o mesmo shape no card de viagens
 * (`trips`) e na pilha de aprovações (`approval_stack`, que só o estende). Mora
 * em `shared/schema` porque é wire de mais de um kind: nenhum kind importa outro.
 */
const ChatUiTripJourneySchema = z.object({
  origin: z.string().nullable(),
  destination: z.string().nullable(),
  startAt: z.string().nullable(),
  endAt: z.string().nullable(),
  startLocal: z.string().nullable().optional(),
  endLocal: z.string().nullable().optional(),
});

/**
 * Wire-honest mirror of `VoucherDetailsSchema` (domain traveler_view): the SSE
 * frame is JSON, so the per-modal date fields arrive at the client as ISO
 * strings — NOT the `Date` that the source schema's `z.coerce.date()` would
 * infer. We reuse the traveler schemas for every non-date field (no drift) and
 * override ONLY the date fields to `z.string()`, so the inferred type matches
 * what the producer ships and the client reads. RENTAL/MOBILITY carry no dates
 * → reused verbatim.
 */
const ChatUiVoucherFlightSegmentSchema = FlightSegmentSchema.extend({
  departAt: z.string().nullable(),
  arriveAt: z.string().nullable(),
});
const ChatUiVoucherFlightDetailsSchema = z.object({
  kind: z.literal('FLIGHT'),
  segments: z.array(ChatUiVoucherFlightSegmentSchema),
});
const ChatUiVoucherHotelDetailsSchema = HotelDetailsSchema.extend({
  checkIn: z.string().nullable(),
  checkOut: z.string().nullable(),
  cancellationPolicy: z
    .object({
      startAt: z.string().nullable(),
      endAt: z.string().nullable(),
      value: z.number().nullable(),
    })
    .nullable(),
});
const ChatUiVoucherBusSegmentSchema = BusSegmentSchema.extend({
  departAt: z.string().nullable(),
  arriveAt: z.string().nullable(),
});
const ChatUiVoucherBusDetailsSchema = z.object({
  kind: z.literal('BUS'),
  segments: z.array(ChatUiVoucherBusSegmentSchema),
});
const ChatUiVoucherDetailsSchema = z.discriminatedUnion('kind', [
  ChatUiVoucherFlightDetailsSchema,
  ChatUiVoucherHotelDetailsSchema,
  ChatUiVoucherBusDetailsSchema,
  RentalDetailsSchema,
  MobilityDetailsSchema,
]);

export const ChatUiTripVoucherSchema = z.object({
  id: z.string(),
  voucherCode: z.string().nullable(),
  modal: z.string().nullable(),
  status: VoucherStatusSchema.nullable(),
  supplier: z.string().nullable(),
  amount: z.number().nullable(),
  currency: z.string().nullable(),
  emittedAt: z.string().nullable().optional(),
  reservationNumber: z.string().nullable().optional(),
  requester: z.object({ name: z.string().nullable() }).nullable().optional(),
  journey: ChatUiTripJourneySchema.nullable(),
  details: ChatUiVoucherDetailsSchema.nullable().optional(),
});
export type ChatUiTripVoucher = z.infer<typeof ChatUiTripVoucherSchema>;
